import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../context/AppContext';

function timeAgo(ts) {
  const diff = Date.now() - ts;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return '刚刚';
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  return `${days}天前`;
}

export default function PostCard({ post }) {
  const { state, dispatch, getUser } = useApp();
  const [commentText, setCommentText] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(null);

  const author = getUser(post.userId);
  const isLiked = post.likes.includes(state.currentUserId);
  const isOwn = post.userId === state.currentUserId;

  const handleLike = () => dispatch({ type: 'TOGGLE_LIKE', payload: post.id });
  const handleDelete = () => dispatch({ type: 'DELETE_POST', payload: post.id });
  const handleComment = () => {
    if (!commentText.trim()) return;
    dispatch({ type: 'ADD_COMMENT', payload: { postId: post.id, text: commentText.trim() } });
    setCommentText('');
  };
  const goProfile = () =>
    dispatch({ type: 'SET_VIEW', payload: { view: 'profile', userId: post.userId } });

  return (
    <motion.div
      className="post-card"
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: -300 }}
      layout
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
    >
      <div className="post-header">
        <button className="post-avatar-btn" onClick={goProfile}>
          <div className="post-avatar">
            {author.avatar ? <img src={author.avatar} alt="" /> : <span>{author.name[0]}</span>}
          </div>
        </button>
        <div className="post-meta">
          <button className="post-author" onClick={goProfile}>{author.name}</button>
          <span className="post-time">{timeAgo(post.createdAt)}</span>
        </div>
        {isOwn && (
          <motion.button
            className="btn-delete"
            onClick={handleDelete}
            whileHover={{ scale: 1.2 }}
            whileTap={{ scale: 0.8 }}
          >
            🗑️
          </motion.button>
        )}
      </div>

      {post.content && <p className="post-content">{post.content}</p>}

      {post.images.length > 0 && (
        <div className={`post-images grid-${Math.min(post.images.length, 3)}`}>
          {post.images.map((img, i) => (
            <motion.img
              key={i}
              src={img}
              alt=""
              className="post-img"
              whileHover={{ scale: 1.05 }}
              onClick={() => setShowImageViewer(img)}
            />
          ))}
        </div>
      )}

      <div className="post-actions">
        <motion.button
          className={`btn-action ${isLiked ? 'liked' : ''}`}
          onClick={handleLike}
          whileTap={{ scale: 0.8 }}
        >
          <motion.span
            key={isLiked ? 'liked' : 'not'}
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', stiffness: 500 }}
          >
            {isLiked ? '❤️' : '🤍'}
          </motion.span>
          <span>{post.likes.length || ''}</span>
        </motion.button>
        <motion.button
          className="btn-action"
          onClick={() => setShowComments(!showComments)}
          whileTap={{ scale: 0.9 }}
        >
          💬 <span>{post.comments.length || ''}</span>
        </motion.button>
      </div>

      {post.likes.length > 0 && (
        <motion.div
          className="post-likes"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          ❤️ {post.likes.map((uid) => getUser(uid)?.name).join('、')}
        </motion.div>
      )}

      <AnimatePresence>
        {showComments && (
          <motion.div
            className="post-comments"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
          >
            {post.comments.map((c) => {
              const commenter = getUser(c.userId);
              return (
                <motion.div
                  key={c.id}
                  className="comment"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                >
                  <span className="comment-author">{commenter?.name}</span>
                  <span className="comment-text">{c.text}</span>
                </motion.div>
              );
            })}
            <div className="comment-input-wrap">
              <input
                className="comment-input"
                placeholder="写评论..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleComment()}
              />
              <motion.button
                className="btn-send"
                onClick={handleComment}
                whileTap={{ scale: 0.9 }}
                disabled={!commentText.trim()}
              >
                发送
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showImageViewer && (
          <motion.div
            className="image-viewer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowImageViewer(null)}
          >
            <motion.img
              src={showImageViewer}
              alt=""
              initial={{ scale: 0.5 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.5 }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
